import {
  AutoCompleteDomain,
  AutoCompleteSearchRequest,
  AutoCompleteSearchResult,
  MetaData,
  OptionItem,
} from "../../models/control";
import {
  AutoCompleteProviderContextResolver,
  createAutoCompleteMetaDataByDomain,
} from "./factory";

const EMPTY_PAGED_RESULT: AutoCompleteSearchResult = {
  items: [],
  totalCount: 0,
};

const hasValidCustomerId = (customerId?: string | number | null): boolean => {
  if (customerId == null) {
    return false;
  }

  if (typeof customerId === "number") {
    return true;
  }

  return customerId.trim() !== "";
};

const createDomainAutoCompleteMetaData = (
  domain: AutoCompleteDomain,
  mode: "legacy" | "paged",
  getContext?: AutoCompleteProviderContextResolver,
): MetaData => {
  const providerMetaData = createAutoCompleteMetaDataByDomain(
    domain,
    getContext,
  );

  return {
    ...(providerMetaData || {}),
    autoCompleteDomain: domain,
    autoCompleteMode: mode,
  };
};

export const createDomainPagedAutoCompleteMetaData = (
  domain: AutoCompleteDomain,
  getContext?: AutoCompleteProviderContextResolver,
): MetaData => {
  return createDomainAutoCompleteMetaData(domain, "paged", getContext);
};

export const createDomainLegacyAutoCompleteMetaData = (
  domain: AutoCompleteDomain,
  getContext?: AutoCompleteProviderContextResolver,
): MetaData => {
  return createDomainAutoCompleteMetaData(domain, "legacy", getContext);
};

export const createCustomerDependentPagedAutoCompleteMetaData = (
  domain: Extract<AutoCompleteDomain, "order" | "workOrder" | "product">,
  resolveCustomerId: () => string | number | null | undefined,
): MetaData => {
  const providerMetaData = createAutoCompleteMetaDataByDomain(domain, () => ({
    customerId: resolveCustomerId(),
  }));

  return {
    ...(providerMetaData || {}),
    autoCompleteDomain: domain,
    autoCompleteMode: "paged",
    searchOptionItemsAsync: async (keyword: string): Promise<OptionItem[]> => {
      const customerId = resolveCustomerId();
      if (!hasValidCustomerId(customerId)) {
        return [];
      }

      return (await providerMetaData?.searchOptionItemsAsync?.(keyword)) || [];
    },
    searchOptionItemsPagedAsync: async (
      keyword: string,
      request: AutoCompleteSearchRequest,
    ): Promise<AutoCompleteSearchResult> => {
      const customerId = resolveCustomerId();
      if (!hasValidCustomerId(customerId)) {
        return EMPTY_PAGED_RESULT;
      }

      return (
        (await providerMetaData?.searchOptionItemsPagedAsync?.(
          keyword,
          request,
        )) || EMPTY_PAGED_RESULT
      );
    },
  };
};
